/**
 * Loader CSS — T-311.
 *
 * Inline stylesheet for the Pulse launcher + iframe overlay. All
 * selectors are prefixed `#muse-noir-*` and use `all: unset` on
 * interactive elements so retailer theme CSS can't bleed in.
 *
 * Visual spec stamped 2026-05-01:
 *   - 64px circle (mobile 56px), bottom-right anchored
 *   - Ink ground #0a0a0a, silver ring 1px rgba(255,255,255,0.4)
 *   - `M` letter, weight 200, letter-spacing 0.05em, silver
 *   - Sonar pulse: 1s expansion, every 4s
 *   - Hover scales 1.05x; pulse pauses
 *   - Tooltip 11px small caps, ink ground silver text
 *   - Auto-tease at 8s idle: tooltip flashes 1.2s, one-shot
 *   - Entrance: 1500ms after script run, fade + 8px translateY
 *   - Reduced-motion: all animations disabled
 */

export const LOADER_CSS = `
@keyframes muse-noir-pulse {
  0% {
    box-shadow:
      0 0 0 0 rgba(255, 255, 255, 0.4),
      0 4px 14px rgba(0, 0, 0, 0.45);
  }
  80%, 100% {
    box-shadow:
      0 0 0 16px rgba(255, 255, 255, 0),
      0 4px 14px rgba(0, 0, 0, 0.45);
  }
}
@keyframes muse-noir-entrance {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes muse-noir-tease {
  0%, 100% { opacity: 0; transform: translateY(2px); }
  20%, 80% { opacity: 1; transform: translateY(0); }
}
@keyframes muse-noir-overlay-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

#muse-noir-launcher {
  all: unset;
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 999998;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #0a0a0a;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 1px solid rgba(255, 255, 255, 0.4);
  transition: transform 200ms ease-out, opacity 200ms ease-out;
  animation:
    muse-noir-entrance 400ms cubic-bezier(0.22, 1, 0.36, 1) 1500ms backwards,
    muse-noir-pulse 4s ease-out 2000ms infinite;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
#muse-noir-launcher:hover {
  transform: scale(1.05);
  animation-play-state: running, paused;
}
#muse-noir-launcher:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.92);
  outline-offset: 4px;
}
#muse-noir-launcher .muse-noir-mark {
  font-size: 24px;
  font-weight: 200;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1;
}

/* Unavailable state (outer health gate failed) — visibly dimmed,
   click triggers a "catching our breath" tooltip instead of opening. */
#muse-noir-launcher[data-state="unavailable"] {
  opacity: 0.4;
  cursor: default;
  animation: muse-noir-entrance 400ms cubic-bezier(0.22, 1, 0.36, 1) 1500ms backwards;
}
#muse-noir-launcher[data-state="unavailable"]:hover {
  transform: none;
}

/* Mobile breakpoint: smaller pill, tighter inset. */
@media (max-width: 767px) {
  #muse-noir-launcher {
    width: 56px;
    height: 56px;
    bottom: 16px;
    right: 16px;
  }
  #muse-noir-launcher .muse-noir-mark {
    font-size: 22px;
  }
}

/* Tooltip — appears on hover and during the 8s auto-tease flash. */
#muse-noir-tooltip {
  position: fixed;
  z-index: 999998;
  padding: 8px 14px;
  background: rgba(10, 10, 10, 0.95);
  color: rgba(255, 255, 255, 0.92);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  pointer-events: none;
  opacity: 0;
  transition: opacity 200ms ease-out;
  white-space: nowrap;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
#muse-noir-tooltip[data-visible="true"] {
  opacity: 1;
}
#muse-noir-tooltip[data-mode="tease"] {
  animation: muse-noir-tease 1200ms ease-out forwards;
}

/* Iframe overlay. Full-viewport on mobile; padded with backdrop on
   desktop. Z-index sits one above launcher so click can't fall
   through to the host page. */
#muse-noir-overlay {
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  animation: muse-noir-overlay-in 250ms ease-out;
}
#muse-noir-overlay-bg {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(2px);
}
#muse-noir-iframe {
  position: relative;
  flex: 1;
  border: none;
  background: #0a0a0a;
  z-index: 1;
}
#muse-noir-close {
  all: unset;
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(10, 10, 10, 0.85);
  color: rgba(255, 255, 255, 0.92);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: background 150ms ease;
}
#muse-noir-close:hover {
  background: rgba(20, 20, 20, 0.95);
}

@media (prefers-reduced-motion: reduce) {
  #muse-noir-launcher {
    animation: none;
  }
  #muse-noir-launcher:hover {
    transform: none;
  }
  #muse-noir-tooltip[data-mode="tease"] {
    animation: none;
  }
  #muse-noir-overlay {
    animation: none;
  }
}
`;

export function injectStylesheet(): void {
  if (document.getElementById("muse-noir-loader-styles")) return;
  const style = document.createElement("style");
  style.id = "muse-noir-loader-styles";
  style.textContent = LOADER_CSS;
  document.head.appendChild(style);
}
